import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import {
  DataAccessService,
  FindDivisionsDocument,
  FindDivisionsQuery,
  SearchUsersForAutoCompleteDocument,
  SearchUsersForAutoCompleteQueryVariables,
  SearchUsersForAutoCompleteQuery,
  User,
  FindDivisionByIdQuery,
  FindDivisionByIdDocument,
  GetPlansDocument,
  GetPlansQuery,
  FindDivisionsWithHomesQuery,
  FindDivisionsWithHomesDocument,
  GetPhasesByLotIdDocument,
  GetPhasesByLotIdQuery,
  FindUsersForContactAutocompleteQuery,
  FindUsersForContactAutocompleteDocument,
  FindUsersForContactAutocompleteQueryVariables
} from '@tripointe/tripointe-cms/data-access';
import { PaginatedResponse } from '@tripointe/tripointe-cms/types';

@Injectable({
  providedIn: 'root'
})
export class CommonEntitiesService {
  loading$ = new BehaviorSubject(false);
  divisions$ = new BehaviorSubject([]);
  communities$ = new BehaviorSubject([]);
  plans$ = new BehaviorSubject([]);

  constructor(private dataAccess: DataAccessService) {}

  setLoading(isLoading: boolean): void {
    this.loading$.next(isLoading);
  }

  // divisions
  loadDivisions(
    payload: {
      withCommunities?: boolean;
      withPhases?: boolean;
      archived?: boolean;
    } = {
      withCommunities: false,
      withPhases: false
    }
  ): void {
    this.setLoading(true);
    this.dataAccess
      .get<FindDivisionsQuery>(FindDivisionsDocument, payload)
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe(({ data: { findDivisions: response } }) => {
        this.setDivisions(response.divisions);
      });
  }

  setDivisions(divisions: any): void {
    this.divisions$.next(divisions);
  }

  getDivisions(
    payload: { withCommunities?: boolean; withPhases?: boolean; archived?: boolean } = {
      withCommunities: false,
      withPhases: false
    }
  ): Observable<ApolloQueryResult<FindDivisionsQuery>> {
    this.setLoading(true);

    return this.dataAccess
      .get<FindDivisionsQuery>(FindDivisionsDocument, payload)
      .pipe(finalize(() => this.setLoading(false)));
  }

  getDivisionById(id: number): Observable<ApolloQueryResult<FindDivisionByIdQuery>> {
    this.setLoading(true);

    return this.dataAccess
      .get<FindDivisionByIdQuery>(FindDivisionByIdDocument, { id })
      .pipe(finalize(() => this.setLoading(false)));
  }

  // plans
  loadPlans(
    payload: { communityIds: number[]; withElevations?: boolean; archived?: boolean } = {
      communityIds: [],
      withElevations: false
    }
  ): Observable<ApolloQueryResult<GetPlansQuery>> {
    this.setLoading(true);

    return this.dataAccess
      .get<GetPlansQuery>(GetPlansDocument, payload)
      .pipe(finalize(() => this.setLoading(false)));
  }

  searchUsers(
    queryOptions: SearchUsersForAutoCompleteQueryVariables
  ): Observable<PaginatedResponse<User>> {
    this.setLoading(true);

    return this.dataAccess
      .get<SearchUsersForAutoCompleteQuery>(SearchUsersForAutoCompleteDocument, queryOptions)
      .pipe(
        map(({ data: { findUsers: response } }) => response),
        finalize(() => this.setLoading(false))
      );
  }

  findDivisionsWithHomes(): Observable<ApolloQueryResult<FindDivisionsWithHomesQuery>> {
    this.setLoading(true);

    return this.dataAccess
      .get<FindDivisionsWithHomesQuery>(FindDivisionsWithHomesDocument)
      .pipe(finalize(() => this.setLoading(false)));
  }

  loadPhasesByLotId(lotId: number): Observable<ApolloQueryResult<GetPhasesByLotIdQuery>> {
    return this.dataAccess
      .get<GetPhasesByLotIdQuery>(GetPhasesByLotIdDocument, { lotId, archived: false })
      .pipe(finalize(() => this.setLoading(false)));
  }

  searchUsersForContactAutoComplete(
    queryArgs: FindUsersForContactAutocompleteQueryVariables
  ): Observable<ApolloQueryResult<FindUsersForContactAutocompleteQuery>> {
    return this.dataAccess
      .get<FindUsersForContactAutocompleteQuery>(FindUsersForContactAutocompleteDocument, queryArgs)
      .pipe(finalize(() => this.setLoading(false)));
  }
}
